import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  FormControlLabel,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Chip,
  Divider,
  Switch,
  Alert,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { layoutCSS } from "../component/common/commonStyle";
import {
  useGetEmailTemplateQuery,
  useSendEmailCampaignMutation,
} from "../store/services/emailApi";
import DOMPurify from "dompurify";

// ── Mock domains (not from API) ────────────────────────────────────────────────
const MOCK_DOMAINS = ["fabmediatech.com"];

// ── Helper to extract preview text from HTML ───────────────────────────────────
const extractPreviewText = (htmlContent) => {
  if (!htmlContent) return "No preview available";
  const text = htmlContent
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 100 ? text.substring(0, 100) + "…" : text;
};

// ── Helper to extract subject from HTML (first heading or first line) ──────────
const extractSubject = (htmlContent) => {
  if (!htmlContent) return "Email Template";
  const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
  if (h1Match) return h1Match[1].replace(/<[^>]*>/g, "").trim();
  const h2Match = htmlContent.match(/<h2[^>]*>(.*?)<\/h2>/i);
  if (h2Match) return h2Match[1].replace(/<[^>]*>/g, "").trim();
  const pMatch = htmlContent.match(/<p[^>]*>(.*?)<\/p>/i);
  if (pMatch) return pMatch[1].replace(/<[^>]*>/g, "").trim();
  return "Email Template";
};

// ── Shared styles ───────────────────────────────────────────────────────────────
const sectionTitle = {
  fontWeight: 600,
  fontSize: "0.82rem",
  color: "#333",
  mb: 1.5,
};
const labelSx = { fontSize: "0.78rem" };

// ── Section header with step number ──────────────────────────────────────────
const SectionHeader = ({ step, icon, title, complete }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: complete ? "#4caf50" : "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {complete ? (
        <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
      ) : (
        <Typography
          sx={{ fontSize: "0.65rem", color: "#fff", fontWeight: 700 }}
        >
          {step}
        </Typography>
      )}
    </Box>
    {icon && React.cloneElement(icon, { sx: { fontSize: 15, color: "#555" } })}
    <Typography sx={sectionTitle}>{title}</Typography>
  </Box>
);

// ── Email preview panel (renders real HTML) ────────────────────────────────────
const EmailPreviewPanel = ({
  senderName,
  senderPrefix,
  domain,
  subject,
  selectedTemplate,
}) => {
  const fromAddress =
    senderPrefix && domain ? `${senderPrefix}@${domain}` : "sender@domain.com";
  const displayName = senderName || "Sender Name";
  const previewSubject = subject || "Your email subject here";
  const tpl = selectedTemplate;
  const sanitizedHtml = tpl?.rawHtml ? DOMPurify.sanitize(tpl.rawHtml) : null;

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
          px: 1.5,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: "#555",
            letterSpacing: 0.5,
          }}
        >
          EMAIL PREVIEW
        </Typography>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <EmailIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          <Typography
            sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#333" }}
          >
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: "#888" }}>
            &lt;{fromAddress}&gt;
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#222", mt: 0.5 }}
        >
          {previewSubject}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          p: 2,
          backgroundColor: tpl?.previewBg || "#f9f9f9",
          overflowY: "auto",
        }}
      >
        {tpl ? (
          sanitizedHtml ? (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "6px",
                p: 2,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                "& img": { maxWidth: "100%", height: "auto" },
                "& *": { maxWidth: "100%" },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: "center", color: "#888" }}>
              <Typography sx={{ fontSize: "0.75rem" }}>
                No HTML content available
              </Typography>
            </Box>
          )
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={1}
          >
            <EmailIcon sx={{ fontSize: 36, color: "#ddd" }} />
            <Typography sx={{ fontSize: "0.72rem", color: "#bbb" }}>
              Select a template
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#ccc" }}>
              to see preview
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Template card with miniature HTML preview ──────────────────────────────────
const TemplateCard = ({ tpl, selected, onSelect }) => {
  const sanitizedHtml = tpl?.rawHtml ? DOMPurify.sanitize(tpl.rawHtml) : null;

  return (
    <Box
      onClick={() => onSelect(tpl)}
      sx={{
        minWidth: 160,
        maxWidth: 160,
        border: `2px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
        borderRadius: "8px",
        overflow: "hidden",
        cursor: "pointer",
        flexShrink: 0,
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: selected ? "0 0 0 3px rgba(25,118,210,0.15)" : "none",
        "&:hover": {
          borderColor: "#1976d2",
          boxShadow: "0 0 0 3px rgba(25,118,210,0.1)",
        },
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          height: 80,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundColor: tpl.previewBg || "#f5f5f5",
        }}
      >
        {selected && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              backgroundColor: "#1976d2",
              borderRadius: "50%",
              width: 18,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <DoneIcon sx={{ fontSize: 11, color: "#fff" }} />
          </Box>
        )}
        {sanitizedHtml ? (
          <Box
            sx={{
              transform: "scale(0.25)",
              transformOrigin: "top left",
              width: "400%",
              height: "400%",
              pointerEvents: "none",
              "& *": { pointerEvents: "none" },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : (
          <Box sx={{ p: 1 }}>
            <Box
              sx={{
                height: 4,
                width: 30,
                backgroundColor: "rgba(0,0,0,0.15)",
                borderRadius: 1,
                mb: 1,
              }}
            />
            <Box
              sx={{
                height: 3,
                width: "80%",
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: 1,
                mb: 0.5,
              }}
            />
            <Box
              sx={{
                height: 3,
                width: "60%",
                backgroundColor: "rgba(0,0,0,0.08)",
                borderRadius: 1,
              }}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "#333",
            mb: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {tpl.name}
        </Typography>
        <Chip
          label={tpl.category}
          size="small"
          sx={{ fontSize: "0.6rem", height: 16 }}
        />
      </Box>
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const CreateEmailCampaign = () => {
  const navigate = useNavigate();

  // RTK Query: fetch templates and send campaign mutation
  const {
    data: templatesData,
    isLoading: templatesLoading,
    error: templatesError,
  } = useGetEmailTemplateQuery();
  const [sendCampaign, { isLoading: isSending }] =
    useSendEmailCampaignMutation();

  // Transform API templates to UI format
  const apiTemplates = useMemo(() => {
    if (!templatesData?.data) return [];
    return templatesData.data.map((tpl) => ({
      id: tpl.email_template_id,
      name: tpl.email_template_name,
      category: "Custom",
      subject: extractSubject(tpl.email_template_data),
      previewText: extractPreviewText(tpl.email_template_data),
      previewBg: "#f5f5f5",
      rawHtml: tpl.email_template_data,
    }));
  }, [templatesData]);

  // Campaign Details
  const [campaignName, setCampaignName] = useState("");

  // Audience
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Content / Sender
  const [senderName, setSenderName] = useState("");
  const [senderPrefix, setSenderPrefix] = useState("");
  const [domain, setDomain] = useState("fabmediatech.com");
  const [subject, setSubject] = useState("");

  // Template
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openTemplateDrawer, setOpenTemplateDrawer] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePage, setTemplatePage] = useState(0);
  const [templateRowsPerPage, setTemplateRowsPerPage] = useState(5);

  // Schedule
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");

  // Split scheduler (send in batches)
  const [enableSplit, setEnableSplit] = useState(false);
  const [batchSize, setBatchSize] = useState("");
  const [timeInterval, setTimeInterval] = useState("");

  const minDateTime = new Date().toISOString().slice(0, 16);

  const filteredTemplates = apiTemplates.filter(
    (t) =>
      t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
      t.category.toLowerCase().includes(templateSearch.toLowerCase()),
  );

  const isCampaignComplete = !!campaignName;
  const isAudienceComplete = recipientsCount > 0;
  const isContentComplete =
    !!senderName && !!senderPrefix && !!domain && !!subject;
  const isTemplateComplete = !!selectedTemplate;

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      if (!content || content.trim().length === 0) {
        toast.error("File doesn't contain any emails");
        setIsUploading(false);
        return;
      }
      const rows = content
        .split(/\r?\n/)
        .filter((line) => line.trim().length > 0);
      if (rows.length === 0) {
        toast.error("File doesn't contain any emails");
        setIsUploading(false);
        return;
      }
      setUploadedFile(file);
      setRecipientsCount(rows.length);
      toast.success(`Success! ${rows.length} recipients found.`);
      setIsUploading(false);
    };
    reader.onerror = () => {
      toast.error("Error reading file");
      setIsUploading(false);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!campaignName) {
      toast.error("Please enter a campaign name");
      return;
    }
    if (!uploadedFile) {
      toast.error("Please upload a recipient list");
      return;
    }
    if (!campaignName?.trim()) {
      toast.error("Campaign name is required");
      return;
    }

    if (!uploadedFile) {
      toast.error("Please upload a recipient list");
      return;
    }

    if (!senderName?.trim()) {
      toast.error("Sender name is required");
      return;
    }

    if (!senderPrefix?.trim()) {
      toast.error("Sender prefix is required (e.g. noreply)");
      return;
    }

    if (!domain?.trim()) {
      toast.error("Please select a domain");
      return;
    }

    if (!subject?.trim()) {
      toast.error("Subject is required");
      return;
    }

    if (!selectedTemplate) {
      toast.error("Please select an email template");
      return;
    }

    if (enableSchedule && !scheduleDateTime) {
      toast.error("Please select schedule time");
      return;
    }
    // Validate split scheduler inputs when enabled
    if (enableSplit) {
      if (!batchSize || isNaN(Number(batchSize)) || Number(batchSize) <= 0) {
        toast.error("Please enter a valid emails-per-batch value");
        return;
      }
      if (!timeInterval || isNaN(Number(timeInterval)) || Number(timeInterval) <= 0) {
        toast.error("Please enter a valid time-interval (minutes)");
        return;
      }
    }
    if (!selectedTemplate) {
      toast.error("Please select an email template");
      return;
    }
    if (enableSchedule && !scheduleDateTime) {
      toast.error("Please select a schedule date and time");
      return;
    }

    const formData = new FormData();
    formData.append("campaign_name", campaignName);
    formData.append("template_id", selectedTemplate.id);
    formData.append("subject", subject.trim());
    formData.append("sender_name", senderName);
    formData.append("from", `${senderPrefix}@${domain}`);
    formData.append("file", uploadedFile);
    if (enableSchedule) {
      formData.append("schedule_time", scheduleDateTime);
    }
    // If split sending enabled, include split params
    if (enableSplit) {
      formData.append("post_via", "Split");
      formData.append("batch_size", batchSize);
      formData.append("time_interval", timeInterval);
    }

    try {
      const response = await sendCampaign(formData).unwrap();
      toast.success(response?.message || "Campaign created successfully!");
      navigate("/email-campaign-report"); // or wherever you want to redirect
    } catch (error) {
      toast.error(error?.data?.message || "Failed to create campaign");
    }
  };

  return (
    <Box sx={layoutCSS}>
      {/* Page Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <IconButton
          size="small"
          onClick={() => navigate(-1)}
          sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#222" }}>
            Create Email Campaign
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
            Fill in the details below to launch your campaign
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT COLUMN */}
        <Grid size={8}>
          {/* 1 · Campaign Details */}
          <Card sx={{ p: 2, mb: 2, borderRadius: "8px" }}>
            <SectionHeader
              step="1"
              icon={<EditIcon />}
              title="Campaign Details"
              complete={isCampaignComplete}
            />
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Campaign Name *"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: labelSx }}
                  inputProps={{ sx: labelSx }}
                />
              </Grid>
            </Grid>
          </Card>

          {/* 2 · Audience Selection */}
          <Card sx={{ p: 2, mb: 2, borderRadius: "8px" }}>
            <SectionHeader
              step="2"
              icon={<PeopleIcon />}
              title="Audience Selection"
              complete={isAudienceComplete}
            />
            <Box
              sx={{
                border: "1px dashed #ccc",
                borderRadius: "6px",
                p: 1,
                backgroundColor: "#fafafa",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "#f0f7ff",
                },
              }}
            >
              <input
                type="file"
                accept=".csv"
                id="csv-upload"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
              <label
                htmlFor="csv-upload"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CloudUploadIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                  <Box sx={{ flex: 1, textAlign: "left" }}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: "#333",
                        lineHeight: 1,
                      }}
                    >
                      {uploadedFile ? uploadedFile.name : "Upload CSV file"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.62rem", color: "#888", mt: 0.2 }}
                    >
                      One email per row
                    </Typography>
                  </Box>
                  <Button
                    component="span"
                    variant="contained"
                    size="small"
                    disabled={isUploading}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.65rem",
                      py: 0.3,
                      px: 1,
                      height: 24,
                      backgroundColor: "#1976d2",
                      boxShadow: "none",
                    }}
                  >
                    {isUploading ? (
                      <CircularProgress
                        size={12}
                        sx={{ mr: 1, color: "inherit" }}
                      />
                    ) : null}
                    {uploadedFile ? "Change" : "Browse"}
                  </Button>
                </Box>
              </label>
            </Box>
            {recipientsCount > 0 && (
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: "#e8f5e9",
                  borderRadius: "6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <PeopleIcon sx={{ fontSize: 14, color: "#4caf50" }} />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#4caf50",
                    fontWeight: 600,
                  }}
                >
                  {recipientsCount.toLocaleString()} recipients found
                </Typography>
              </Box>
            )}
          </Card>

          {/* 3 · Content / Sender */}
          <Card sx={{ p: 2, mb: 2, borderRadius: "8px" }}>
            <SectionHeader
              step="3"
              icon={<SettingsIcon />}
              title="Email Content"
              complete={isContentComplete}
            />
            <Grid container spacing={2} mb={1.5}>
              <Grid size={4}>
                <TextField
                  label="Sender Name *"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ sx: labelSx }}
                  inputProps={{ sx: labelSx }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Sender Prefix *"
                  value={senderPrefix}
                  onChange={(e) => setSenderPrefix(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g. noreply"
                  InputLabelProps={{ sx: labelSx }}
                  inputProps={{ sx: labelSx }}
                  helperText={
                    senderPrefix && domain ? `→ ${senderPrefix}@${domain}` : ""
                  }
                  FormHelperTextProps={{
                    sx: { fontSize: "0.65rem", mt: 0.25 },
                  }}
                />
              </Grid>
              <Grid size={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={labelSx}>Domain *</InputLabel>
                  <Select
                    label="Domain *"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    sx={{ fontSize: "0.78rem" }}
                  >
                    {MOCK_DOMAINS.map((d) => (
                      <MenuItem key={d} value={d} sx={{ fontSize: "0.78rem" }}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Subject *"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter your email subject line"
                  InputLabelProps={{ sx: labelSx }}
                  inputProps={{ sx: labelSx }}
                />
              </Grid>
            </Grid>
          </Card>

          {/* 4 · Template Body */}
          <Card sx={{ p: 2, mb: 2, borderRadius: "8px" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1.5}
            >
              <SectionHeader
                step="4"
                icon={<EmailIcon />}
                title="Email Body / Template"
                complete={isTemplateComplete}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  fontSize: "0.72rem",
                  py: 0.4,
                  px: 1.5,
                  textTransform: "none",
                  mb: 1.5,
                }}
                onClick={() => setOpenTemplateDrawer(true)}
              >
                Browse All Templates
              </Button>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "#888", mb: 1 }}>
              {selectedTemplate
                ? `Selected: ${selectedTemplate.name}`
                : "Quick select from your templates:"}
            </Typography>
            {templatesLoading ? (
              <Box sx={{ display: "flex", gap: 1.5, pb: 1 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width={160}
                    height={120}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
            ) : templatesError ? (
              <Alert severity="error" sx={{ fontSize: "0.75rem" }}>
                Failed to load templates
              </Alert>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  overflowX: "auto",
                  pb: 1,
                  "&::-webkit-scrollbar": { height: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ddd",
                    borderRadius: 2,
                  },
                }}
              >
                {apiTemplates.map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    selected={selectedTemplate?.id === tpl.id}
                    onSelect={(t) => {
                      const isSame = selectedTemplate?.id === t.id;

                      if (isSame) {
                        setSelectedTemplate(null);
                      } else {
                        setSelectedTemplate(t);
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Card>

          {/* 5 · Schedule */}
          <Card sx={{ p: 2, mb: 2, borderRadius: "8px" }}>
            <SectionHeader
              step="5"
              icon={<SettingsIcon />}
              title="Schedule"
              complete={enableSchedule && !!scheduleDateTime}
            />
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableSchedule}
                    onChange={(e) => setEnableSchedule(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.78rem" }}>
                    Schedule for later
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              {enableSchedule && (
                <TextField
                  label="Date & Time"
                  type="datetime-local"
                  value={scheduleDateTime}
                  onChange={(e) => setScheduleDateTime(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true, sx: labelSx }}
                  inputProps={{ min: minDateTime, sx: labelSx }}
                  sx={{ width: 200 }}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableSplit}
                    onChange={(e) => setEnableSplit(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.78rem" }}>
                    Send in batches (Split scheduler)
                  </Typography>
                }
                sx={{ m: 0 }}
              />

              {enableSplit && (
                <Box display="flex" gap={1} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    label="Emails per batch"
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    size="small"
                    InputLabelProps={{ sx: labelSx }}
                    inputProps={{ min: 1, sx: labelSx }}
                    sx={{ width: 180 }}
                  />
                  <TextField
                    label="After every (minutes)"
                    type="number"
                    value={timeInterval}
                    onChange={(e) => setTimeInterval(e.target.value)}
                    size="small"
                    InputLabelProps={{ sx: labelSx }}
                    inputProps={{ min: 1, sx: labelSx }}
                    sx={{ width: 200 }}
                  />
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* RIGHT COLUMN — Preview */}
        <Grid size={4}>
          <Box sx={{ position: "sticky", top: 16 }}>
            <Typography sx={{ ...sectionTitle, mb: 1 }}>
              Live Preview
            </Typography>
            <Box sx={{ height: 520 }}>
              <EmailPreviewPanel
                senderName={senderName}
                senderPrefix={senderPrefix}
                domain={domain}
                subject={subject}
                selectedTemplate={selectedTemplate}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 1,
          p: 1.5,
          backgroundColor: "#fff",
          borderRadius: "6px",
          border: "1px solid #e0e0e0",
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={isSending}
          sx={{
            minWidth: 120,
            textTransform: "none",
            fontSize: "0.78rem",
            py: 0.6,
          }}
        >
          {isSending ? (
            <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
          ) : null}
          {enableSchedule ? "Schedule Campaign" : "Send Campaign"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(-1)}
          sx={{
            minWidth: 80,
            textTransform: "none",
            fontSize: "0.78rem",
            py: 0.6,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="text"
          size="small"
          sx={{
            minWidth: 80,
            textTransform: "none",
            fontSize: "0.78rem",
            py: 0.6,
            ml: "auto",
            color: "#888",
          }}
        >
          Save as Draft
        </Button>
      </Box>

      {/* Template Selection Drawer */}
      <Drawer
        anchor="right"
        open={openTemplateDrawer}
        onClose={() => setOpenTemplateDrawer(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 500 } } }}
      >
        <Box
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Select Template
            </Typography>
            <IconButton
              onClick={() => setOpenTemplateDrawer(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField
            label="Search templates..."
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            value={templateSearch}
            onChange={(e) => {
              setTemplateSearch(e.target.value);
              setTemplatePage(0);
            }}
          />
          {templatesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : templatesError ? (
            <Alert severity="error">Error loading templates</Alert>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  flex: 1,
                  borderRadius: "6px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          width: 60,
                          py: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        Select
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.75rem" }}
                      >
                        Template Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          width: 110,
                          py: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        Category
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{
                            py: 4,
                            fontSize: "0.75rem",
                            color: "#888",
                            fontStyle: "italic",
                          }}
                        >
                          No templates found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTemplates
                        .slice(
                          templatePage * templateRowsPerPage,
                          templatePage * templateRowsPerPage +
                            templateRowsPerPage,
                        )
                        .map((tpl) => (
                          <TableRow
                            key={tpl.id}
                            hover
                            onClick={() => setSelectedTemplate(tpl)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor:
                                selectedTemplate?.id === tpl.id
                                  ? "#e3f2fd"
                                  : "inherit",
                            }}
                          >
                            <TableCell sx={{ py: 1 }}>
                              <Radio
                                size="small"
                                checked={selectedTemplate?.id === tpl.id}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Typography
                                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                              >
                                {tpl.name}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.68rem", color: "#888" }}
                              >
                                {tpl.subject}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Chip
                                label={tpl.category}
                                size="small"
                                sx={{ fontSize: "0.65rem", height: 18 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
              >
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenTemplateDrawer(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!selectedTemplate}
                    onClick={() => setOpenTemplateDrawer(false)}
                  >
                    Select
                  </Button>
                </Box>
                <TablePagination
                  rowsPerPageOptions={[5, 10]}
                  component="div"
                  count={filteredTemplates.length}
                  rowsPerPage={templateRowsPerPage}
                  page={templatePage}
                  onPageChange={(_, p) => setTemplatePage(p)}
                  onRowsPerPageChange={(e) => {
                    setTemplateRowsPerPage(parseInt(e.target.value, 10));
                    setTemplatePage(0);
                  }}
                  sx={{
                    "& .MuiToolbar-root": { minHeight: 36 },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      { fontSize: "0.72rem" },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default CreateEmailCampaign;
